import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';

import TitleContainer from '../common/components/TitleContainer/TitleContainer';
import ButtonContainer from '../common/components/Button/ButtonContainer';
import Button, { ButtonStyle } from '../common/components/Button/Button';
import { FlowConfigContext } from '../providers/FlowConfigContext';
import Container from '../common/components/Container/Container';
import { ValidationContext } from '../providers/ValidationContext';
import AcceptTermsStep from '../providers/validation/steps/AcceptTermsStep';
import withRouteAnalytics from '../services/analytics/withRouteAnalytics';
import { Screens } from '../services/analytics/screens';
import { getGknLocalisedString } from '../providers/GatekeeperNetworkService/utils';
import { GatekeeperNetworkServiceContext } from '../providers/GatekeeperNetworkService/GatekeeperNetworkServiceContext';

function DeclarationsScreen(): JSX.Element {
  const { t } = useTranslation();
  const [selfDeclarationText, setSelfDeclarationText] = useState<string>('');
  const [selfDeclarationDisplayText, setSelfDeclarationDisplayText] = useState<string>('');
  const {
    acceptedDeclaration,
    navigateToNextFlow,
    setAcceptedDeclaration,
    flowCancelled,
    processAcceptedTermsForUpload,
  } = useContext(FlowConfigContext);
  const { verificationStarted, processStep, getRouteForNextStep } = useContext(ValidationContext);
  const { gknServiceData } = useContext(GatekeeperNetworkServiceContext);

  const navigate = useNavigate();

  useEffect(() => {
    const selfDeclaration = getGknLocalisedString(gknServiceData!, 'acceptedDeclaration');
    if (selfDeclaration) {
      setSelfDeclarationText(selfDeclaration);
      // Clean up the string to replace \n with new lines
      const formattedText = selfDeclaration.replace(/\\n/g, '\n');
      setSelfDeclarationDisplayText(formattedText);
    }
  }, []);

  useEffect(() => {
    // do not use validation context if no verification started
    if (!acceptedDeclaration || verificationStarted()) {
      return;
    }

    navigateToNextFlow();
  }, [acceptedDeclaration]);

  useEffect(() => {
    // process step and navigate with validation context if validation started
    if (!acceptedDeclaration || !verificationStarted()) {
      return;
    }

    processStep(new AcceptTermsStep(processAcceptedTermsForUpload()))
      .then(getRouteForNextStep)
      .then((route) => route && navigate(route));
  }, [acceptedDeclaration]);

  const handleClick = async () => {
    setAcceptedDeclaration(selfDeclarationText);
  };

  const handleCancel = async () => {
    flowCancelled();
  };

  return (
    <Container>
      <TitleContainer marginBottomClass="mb-2">
        <h1 className="mb-2">{t('screens.captchaPass.terms.title')}</h1>
      </TitleContainer>
      <div data-testid="DECLARATIONS_PANEL" className="mb-4 flex-grow-1 flex-shrink-0 declarations text-textParagraph">
        <ReactMarkdown linkTarget="_blank">{selfDeclarationDisplayText}</ReactMarkdown>
      </div>

      <ButtonContainer>
        <Button style={ButtonStyle.DEFAULT} disabled={false} onClick={handleClick} dataTestid="CONFIRM_BUTTON">
          <span>{t('screens.captchaPass.terms.confirmLabel')}</span>
        </Button>
        <Button
          style={ButtonStyle.INVERTED}
          extraClasses="mt-2"
          disabled={false}
          onClick={handleCancel}
          dataTestid="CANCEL_BUTTON"
        >
          <span>{t('screens.captchaPass.terms.cancelLabel')}</span>
        </Button>
      </ButtonContainer>
    </Container>
  );
}
export default withRouteAnalytics(DeclarationsScreen, Screens.SELF_DECLARATION);
